import styled from '@emotion/styled';
import { m, SCALE } from '../../../motion';
import { down, up } from '../../../style-utils/media-queries';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  typography,
} from '../../../style-utils/theme';

/**
 * GATED STEPPER — styles (plan S6). The segment bar is the OnboardingProgress
 * bar (onboarding.styled.tsx ProgressSegments) on the same tokens; the step
 * list below it is new.
 *
 * Phone first: below sm the list is replaced by one line of text, so a 320px
 * guest page spends one row on "where am I" and not five. The list is hidden
 * with a max-width query (down) and the line with a min-width one (up), so an
 * environment that applies no media queries at all shows both rather than
 * neither.
 *
 * NO BACKTICKS IN ANY COMMENT INSIDE A STYLED TEMPLATE in this file.
 */

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  min-width: 0;
`;

export const Segments = styled.div`
  display: flex;
  gap: ${spacing.xs};
  width: 100%;
`;

/* A 4px track holding a fill that SCALES in from the left (motion, T.base),
   the OnboardingProgress segment exactly. */
export const Segment = styled.div`
  position: relative;
  flex: 1;
  height: ${spacing.half};
  border-radius: ${radius.pill};
  background: ${palette.line};
  overflow: hidden;
`;

const segmentFill = (p: { $current?: boolean }) => {
  return p.$current ? palette.lagoon : palette.teal;
};

export const SegmentFill = m.div<{ $current?: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: ${radius.pill};
  background: ${segmentFill};
  transform-origin: left center;
  transition: background-color ${duration.base} ${easing.standard};
`;

/* The phone line: "Step 2 of 4 · Identity". Hidden from sm up, where the
   list says the same thing with more room. */
export const PhoneLine = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;

  ${up('sm')} {
    display: none;
  }
`;

export const StepList = styled.ol`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${space(12)}, 1fr));
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;

  ${down('sm')} {
    display: none;
  }
`;

export const StepItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export type StepLook = 'done' | 'active' | 'open' | 'locked';

const markerFill = ({ $look }: { $look: StepLook }): string => {
  switch ($look) {
    case 'active':
      return palette.lagoon;
    case 'done':
      return palette.tealTint;
    default:
      return palette.surfaceMuted;
  }
};

const markerInk = ({ $look }: { $look: StepLook }): string => {
  switch ($look) {
    case 'active':
      return palette.white;
    case 'done':
      return palette.lagoon;
    default:
      return palette.slate;
  }
};

/* The numbered (or ticked) disc. Decorative: the label carries the name and
   the element around it carries the state. */
export const Marker = styled.span<{ $look: StepLook }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${spacing.md};
  height: ${spacing.md};
  border-radius: ${radius.circle};
  background: ${markerFill};
  color: ${markerInk};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard};

  & > svg {
    width: ${spacing.sm};
    height: ${spacing.sm};
  }
`;

const labelInk = ({ $look }: { $look: StepLook }): string => {
  return $look === 'locked' ? palette.slate : palette.ink;
};

/* Weight stays constant across states (design_system.md 9: active is never
   expressed through font-weight); colour and the disc carry it. */
export const StepLabel = styled.span<{ $look: StepLook }>`
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${labelInk};
  text-align: left;
  overflow-wrap: anywhere;
`;

const stepRow = `
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  width: 100%;
  min-width: 0;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs};
  border-radius: ${radius.sm};
`;

/* A done (or the server's current) step that is not on screen: a button that
   takes the guest back to it. Press grammar of the system. */
export const StepButton = styled.button`
  ${stepRow};
  -webkit-appearance: none;
  appearance: none;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover {
    background: ${palette.tealTintSoft};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* The step on screen, or a locked one: plain text, never a control. */
export const StepStatic = styled.span<{ $look: StepLook }>`
  ${stepRow};
  border: 1px solid
    ${({ $look }) => ($look === 'active' ? palette.lineStrong : 'transparent')};
  background: ${({ $look }) =>
    $look === 'active' ? palette.surface : 'transparent'};
  cursor: ${({ $look }) => ($look === 'locked' ? 'not-allowed' : 'default')};
`;

/* Why a locked step is locked, as visible text (UI-CONTRACT 1: a disabled
   control explains itself). */
export const LockedReason = styled.span`
  min-width: 0;
  padding: 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const Content = styled.div`
  width: 100%;
  min-width: 0;
`;
